import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "../stili/AggiornaUtenti.css";

const API_URL = process.env.REACT_APP_API_URL;

export default function AggiornaUtenti() {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  const [utenti, setUtenti] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [search, setSearch] = useState("");
  const [ruoloFiltro, setRuoloFiltro] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [editingId, setEditingId] = useState(null);

  const [formCreate, setFormCreate] = useState({
    username: "",
    password: "",
    ruoli: [],
  });

  const [formEdit, setFormEdit] = useState({
    username: "",
    attivo: true,
    ruoli: [],
  });

  const [newPassword, setNewPassword] = useState("");

  /* =========================
     UTILS
  ========================= */

  const clearMessage = () => setMessage("");

  const generatePassword = (length = 10) => {
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const lower = "abcdefghijkmnopqrstuvwxyz";
    const numbers = "23456789";
    const symbols = "!@#$%&*?";
    const all = upper + lower + numbers + symbols;

    const pick = (chars) => chars[Math.floor(Math.random() * chars.length)];

    let password = [
      pick(upper),
      pick(lower),
      pick(numbers),
      pick(symbols),
    ];

    for (let i = password.length; i < length; i++) {
      password.push(pick(all));
    }

    for (let i = password.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [password[i], password[j]] = [password[j], password[i]];
    }

    return password.join("");
  };

  const formatDateTime = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("it-IT");
  };

  const getRuoloCodice = (ruolo) => {
    if (!ruolo) return "";
    if (typeof ruolo === "string") return ruolo;
    return ruolo.codice || "";
  };

  const getRuoloDescrizione = (ruolo) => {
    if (!ruolo) return "";
    if (typeof ruolo === "string") return ruolo.replace("ROLE_", "");
    return ruolo.descrizione || ruolo.codice?.replace("ROLE_", "") || "";
  };

  const ruoloBadgeClass = (ruolo) => {
    switch (ruolo) {
      case "ROLE_ADMIN":
        return "badge-admin";
      case "ROLE_STAFF":
        return "badge-staff";
      case "ROLE_GENITORE":
        return "badge-genitore";
      default:
        return "";
    }
  };

  /* =========================
     LOAD
  ========================= */

  const loadUtenti = useCallback(async () => {
    try {
      setLoading(true);
      clearMessage();

      let url = `${API_URL}/api/admin/utenti?page=${page}&size=20`;

      if (search.trim()) {
        url += `&q=${encodeURIComponent(search.trim())}`;
      }

      if (ruoloFiltro) {
        url += `&ruolo=${encodeURIComponent(ruoloFiltro)}`;
      }

      const res = await authFetch(url);

      if (!res.ok) {
        throw new Error("Errore nel recupero utenti");
      }

      const data = await res.json();

      setUtenti(Array.isArray(data.content) ? data.content : []);
      setTotalPages(data.totalPages ?? 0);
    } catch (err) {
      console.error(err);
      setMessage("Errore nel caricamento utenti");
      setUtenti([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [authFetch, page, search, ruoloFiltro]);

  useEffect(() => {
    loadUtenti();
  }, [loadUtenti]);

  /* =========================
     CREATE
  ========================= */

  const handleCreateChange = (e) => {
    const { name, value } = e.target;

    setFormCreate((prev) => ({
      ...prev,
      [name]: name === "username" ? value.toUpperCase() : value,
    }));
  };

  const handleCreateRoleChange = (ruolo) => {
    setFormCreate((prev) => {
      const ruoli = prev.ruoli || [];

      if (ruoli.includes(ruolo)) {
        return {
          ...prev,
          ruoli: ruoli.filter((r) => r !== ruolo),
        };
      }

      return {
        ...prev,
        ruoli: [...ruoli, ruolo],
      };
    });
  };

  const handleGenerateCreatePassword = async () => {
    const generated = generatePassword(10);

    setFormCreate((prev) => ({
      ...prev,
      password: generated,
    }));

    try {
      await navigator.clipboard.writeText(generated);
      setMessage("Password generata e copiata negli appunti");
    } catch {
      setMessage("Password generata");
    }
  };

  const creaUtente = async (e) => {
    e.preventDefault();
    clearMessage();

    if (!formCreate.username.trim()) {
      setMessage("Inserisci lo username");
      return;
    }

    if (!formCreate.password.trim()) {
      setMessage("Inserisci la password");
      return;
    }

    if (formCreate.ruoli.length === 0) {
      setMessage("Seleziona almeno un ruolo");
      return;
    }

    try {
      const payload = {
        username: formCreate.username.trim().toUpperCase(),
        password: formCreate.password,
        ruoli: formCreate.ruoli,
      };

      const res = await authFetch(`${API_URL}/api/admin/utenti`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        console.error(errText);
        throw new Error("Errore durante la creazione utente");
      }

      setFormCreate({
        username: "",
        password: "",
        ruoli: [],
      });

      setMessage("Utente creato correttamente");

      if (page !== 0) {
        setPage(0);
      } else {
        await loadUtenti();
      }
    } catch (err) {
      console.error(err);
      setMessage(err.message || "Errore durante la creazione utente");
    }
  };

  /* =========================
     EDIT
  ========================= */

  const startEdit = (u) => {
    setEditingId(u.id);
    setNewPassword("");
    setFormEdit({
      username: u.username || "",
      attivo: u.attivo ?? true,
      ruoli: Array.isArray(u.ruoli) ? u.ruoli.map((r) => getRuoloCodice(r)) : [],
    });
    clearMessage();
  };

  const cancelEdit = () => {
    setEditingId(null);
    setNewPassword("");
    setFormEdit({
      username: "",
      attivo: true,
      ruoli: [],
    });
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormEdit((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "username"
          ? value.toUpperCase()
          : value,
    }));
  };

  const handleEditRoleChange = (ruolo) => {
    setFormEdit((prev) => {
      const ruoli = prev.ruoli || [];

      if (ruoli.includes(ruolo)) {
        return {
          ...prev,
          ruoli: ruoli.filter((r) => r !== ruolo),
        };
      }

      return {
        ...prev,
        ruoli: [...ruoli, ruolo],
      };
    });
  };

  const salvaModifica = async (id) => {
    clearMessage();

    if (!formEdit.username.trim()) {
      setMessage("Inserisci lo username");
      return;
    }

    if ((formEdit.ruoli || []).length === 0) {
      setMessage("Seleziona almeno un ruolo");
      return;
    }

    try {
      const payload = {
        username: formEdit.username.trim().toUpperCase(),
        attivo: formEdit.attivo,
        ruoli: formEdit.ruoli,
      };

      const res = await authFetch(`${API_URL}/api/admin/utenti/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        console.error(errText);
        throw new Error("Errore durante l'aggiornamento utente");
      }

      cancelEdit();
      setMessage("Utente aggiornato correttamente");
      await loadUtenti();
    } catch (err) {
      console.error(err);
      setMessage(err.message || "Errore durante l'aggiornamento utente");
    }
  };

  /* =========================
     PASSWORD CHANGE
  ========================= */

  const handleGenerateNewPassword = async () => {
    const generated = generatePassword(10);
    setNewPassword(generated);

    try {
      await navigator.clipboard.writeText(generated);
      setMessage("Nuova password generata e copiata negli appunti");
    } catch {
      setMessage("Nuova password generata");
    }
  };

  const cambiaPassword = async (id) => {
    try {
      clearMessage();

      if (!newPassword.trim()) {
        setMessage("Inserisci una nuova password");
        return;
      }

      const res = await authFetch(`${API_URL}/api/admin/utenti/${id}/password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          newPassword: newPassword,
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        console.error(errText);
        throw new Error("Errore durante il cambio password");
      }

      setNewPassword("");
      setMessage("Password aggiornata correttamente");
    } catch (err) {
      console.error(err);
      setMessage(err.message || "Errore durante il cambio password");
    }
  };

  /* =========================
     TOGGLE ATTIVO
  ========================= */

  const toggleAttivo = async (u) => {
    try {
      clearMessage();

      const res = await authFetch(
        `${API_URL}/api/admin/utenti/${u.id}/attivo?value=${!u.attivo}`,
        {
          method: "PUT",
        }
      );

      if (!res.ok) {
        const errText = await res.text();
        console.error(errText);
        throw new Error("Errore durante il cambio stato");
      }

      setMessage(
        u.attivo
          ? "Utente disattivato correttamente"
          : "Utente attivato correttamente"
      );

      if (editingId === u.id) {
        cancelEdit();
      }

      await loadUtenti();
    } catch (err) {
      console.error(err);
      setMessage(err.message || "Errore durante il cambio stato");
    }
  };

  /* =========================
     SEARCH / FILTER / PAGING
  ========================= */

  const handleSearchSubmit = async (e) => {
    e.preventDefault();

    if (page !== 0) {
      setPage(0);
      return;
    }

    await loadUtenti();
  };

  const handleRuoloFiltroChange = (e) => {
    setRuoloFiltro(e.target.value);
    setPage(0);
  };

  /* =========================
     RENDER
  ========================= */

  return (
    <div className="utente-container">
      <h2>Gestione Utenti</h2>

      {message && <div className="utente-message">{message}</div>}

      <form className="utente-form" onSubmit={creaUtente}>
        <input
          type="text"
          name="username"
          value={formCreate.username}
          onChange={handleCreateChange}
          placeholder="Username"
          required
        />

        <input
          type="password"
          name="password"
          value={formCreate.password}
          onChange={handleCreateChange}
          placeholder="Password"
          required
        />

        <button
          type="button"
          className="utente-secondary-button"
          onClick={handleGenerateCreatePassword}
        >
          Genera Password
        </button>

        <div className="utente-ruoli-box-up">
          <label>
            <input
              type="checkbox"
              checked={formCreate.ruoli.includes("ROLE_ADMIN")}
              onChange={() => handleCreateRoleChange("ROLE_ADMIN")}
            />
            Admin
          </label>

          <label>
            <input
              type="checkbox"
              checked={formCreate.ruoli.includes("ROLE_STAFF")}
              onChange={() => handleCreateRoleChange("ROLE_STAFF")}
            />
            Staff
          </label>

          <label>
            <input
              type="checkbox"
              checked={formCreate.ruoli.includes("ROLE_GENITORE")}
              onChange={() => handleCreateRoleChange("ROLE_GENITORE")}
            />
            Genitore
          </label>
        </div>

        <button type="submit">Crea Utente</button>
      </form>

      <form className="utente-filtri" onSubmit={handleSearchSubmit}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value.toUpperCase())}
          placeholder="Cerca username..."
        />

        <select value={ruoloFiltro} onChange={handleRuoloFiltroChange}>
          <option value="">Tutti i ruoli</option>
          <option value="ROLE_ADMIN">Admin</option>
          <option value="ROLE_STAFF">Staff</option>
          <option value="ROLE_GENITORE">Genitore</option>
        </select>

        <button type="submit">Cerca</button>
      </form>

      <table className="utente-tabella">
        <thead>
          <tr>
            <th>Username</th>
            <th>Ruoli</th>
            <th>Stato</th>
            <th>Creato il</th>
            <th>Aggiornato il</th>
            <th>Azioni</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan="6">Caricamento...</td>
            </tr>
          ) : utenti.length === 0 ? (
            <tr>
              <td colSpan="6">Nessun utente trovato</td>
            </tr>
          ) : (
            utenti.map((u) => (
              <React.Fragment key={u.id}>
                <tr>
                  <td>{u.username}</td>

                  <td>
                    <div className="utente-badge-wrapper">
                      {u.ruoli?.map((r, index) => {
                        const codice = getRuoloCodice(r);
                        const descrizione = getRuoloDescrizione(r);

                        return (
                          <span
                            key={`${u.id}-${codice}-${index}`}
                            className={`utente-badge ${ruoloBadgeClass(codice)}`}
                          >
                            {descrizione}
                          </span>
                        );
                      })}
                    </div>
                  </td>

                  <td>
                    <span className={u.attivo ? "stato-attivo" : "stato-off"}>
                      {u.attivo ? "ATTIVO" : "DISATTIVO"}
                    </span>
                  </td>

                  <td>{formatDateTime(u.createdAt)}</td>
                  <td>{formatDateTime(u.updatedAt)}</td>

                  <td>
                    <div className="utente-azioni">
                      <button type="button" onClick={() => startEdit(u)}>
                        Modifica
                      </button>

                      <button type="button" onClick={() => toggleAttivo(u)}>
                        {u.attivo ? "Disattiva" : "Attiva"}
                      </button>
                    </div>
                  </td>
                </tr>

                {editingId === u.id && (
                  <tr className="espansione-utente">
                    <td>
                      <input
                        type="text"
                        name="username"
                        value={formEdit.username || ""}
                        onChange={handleEditChange}
                      />
                    </td>

                    <td>
                      <div className="utente-ruoli-box utente-ruoli-box-edit">
                        <label>
                          <input
                            type="checkbox"
                            checked={(formEdit.ruoli || []).includes("ROLE_ADMIN")}
                            onChange={() => handleEditRoleChange("ROLE_ADMIN")}
                          />
                          Admin
                        </label>

                        <label>
                          <input
                            type="checkbox"
                            checked={(formEdit.ruoli || []).includes("ROLE_STAFF")}
                            onChange={() => handleEditRoleChange("ROLE_STAFF")}
                          />
                          Staff
                        </label>

                        <label>
                          <input
                            type="checkbox"
                            checked={(formEdit.ruoli || []).includes("ROLE_GENITORE")}
                            onChange={() => handleEditRoleChange("ROLE_GENITORE")}
                          />
                          Genitore
                        </label>
                      </div>
                    </td>

                    <td>
                      <label className="utente-check-attivo">
                        <input
                          type="checkbox"
                          name="attivo"
                          checked={!!formEdit.attivo}
                          onChange={handleEditChange}
                        />
                        Attivo
                      </label>
                    </td>

                    <td colSpan="3">
                      <div className="utente-azioni-verticali">
                        <button type="button" onClick={() => salvaModifica(u.id)}>
                          Salva
                        </button>

                        <button type="button" onClick={cancelEdit}>
                          Annulla
                        </button>

                        <input
                          type="password"
                          placeholder="Nuova password"
                          value={newPassword || ""}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />

                        <button
                          type="button"
                          className="utente-secondary-button"
                          onClick={handleGenerateNewPassword}
                        >
                          Genera Password
                        </button>

                        <button
                          type="button"
                          onClick={() => cambiaPassword(u.id)}
                        >
                          Cambia Password
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>

      <div className="utente-paginazione">
        <button
          type="button"
          disabled={page === 0}
          onClick={() => setPage((prev) => prev - 1)}
        >
          ←
        </button>

        <span>
          Pagina {totalPages === 0 ? 0 : page + 1} / {totalPages}
        </span>

        <button
          type="button"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage((prev) => prev + 1)}
        >
          →
        </button>
      </div>

      <div className="utente-button-row">
        <button
          className="utente-custom-button"
          type="button"
          onClick={() => navigate("/gestionale")}
        >
          Torna al Gestionale
        </button>
      </div>
    </div>
  );
}